import { publish, subscribe, unsubscribe } from '@wh/message-bus';
import { getDeviceInfo } from './utils';

export interface AuthConfig {
  casUrl: string;
}

export class Auth {
  private config: AuthConfig;

  public accountNumber: string | null = null;
  public serviceTicket: string | null = null;

  constructor(config: AuthConfig) {
    this.config = config;
  }

  private openLoginModal() {
    if (getDeviceInfo().isNative) {
      window.location.href = 'nw-showlogin://';
    } else {
      // open old login
      const loginElement = document.querySelector(
        '#account-section section.account',
      );
      loginElement?.classList.add('-expanded');

      // open new login
      publish('wh.common.command.ui.login.flow.start', {});
    }
  }

  public async getServiceTicket() {
    const controller = new AbortController();
    const abortTimeoutId = setTimeout(() => controller.abort(), 1000);

    try {
      const response = await fetch(this.config.casUrl, {
        method: 'GET',
        headers: {
          'Sec-Fetch-Site': 'same-site',
          'Sec-Fetch-Mode': 'cors',
          'Sec-Fetch-Dest': 'empty',
          Accept: 'application/json',
        },
        credentials: 'include',
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch CAS ticket from ${this.config.casUrl}, status: ${response.status}`,
        );
      }

      const { serviceTicket } = await response.json();

      if (!serviceTicket) {
        throw new Error('Service ticket not found');
      }

      this.serviceTicket = serviceTicket;

      return serviceTicket as string;
    } finally {
      clearTimeout(abortTimeoutId);
    }
  }

  public async login() {
    const subscribeTokens: string[] = [];
    let hasLoggedIn = false;

    const unsubscribeAll = () => {
      subscribeTokens.forEach((token) => unsubscribe(token));
    };

    this.accountNumber = await new Promise<string>((resolve, reject) => {
      subscribeTokens.push(
        subscribe('gotLoginState', (_, payload) => {
          if (payload.isLoggedIn && payload.data.obAccountNumber) {
            unsubscribeAll();
            return resolve(payload.data.obAccountNumber);
          }

          this.openLoginModal();

          subscribeTokens.push(
            subscribe('wh.common.event.session.logIn', (_, payload) => {
              if (payload.state === 'LOGGED_IN') {
                hasLoggedIn = true;
              }
            }),
          );

          subscribeTokens.push(
            subscribe(
              'wh.common.event.ui.login.flow.state.changed',
              (_, payload) => {
                if (payload.state === 'NONE' && !hasLoggedIn) {
                  unsubscribeAll();
                  reject(new Error('Login got cancelled by user'));
                }
              },
            ),
          );
        }),
      );
    });

    return this.accountNumber;
  }

  public subscribeToLogout(callback: () => void) {
    const token = subscribe('gotLoginState', (_, payload) => {
      if (!payload.isLoggedIn && this.accountNumber) {
        callback();

        this.accountNumber = null;
        this.serviceTicket = null;
        unsubscribe(token);
      }
    });
  }
}
